import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, AlertTriangle, ArrowLeft } from 'lucide-react';
import { formatGameDateTime } from '@/utils/timezone';
import { GameStatusInfo } from '@/utils/gameStatusUtils';

interface ExpiredFormBannerProps {
  gameData: any;
  gameStatus: string;
  gameStatusInfo: GameStatusInfo;
}

export const ExpiredFormBanner: React.FC<ExpiredFormBannerProps> = ({
  gameData,
  gameStatus,
  gameStatusInfo
}) => {
  const navigate = useNavigate();

  const getStatusMessage = () => {
    switch (gameStatus) {
      case 'expired':
        return 'This game form has expired and is no longer available for editing.';
      case 'missed':
        return 'The deadline for this game form has passed.';
      case 'completed':
        return 'This game has been completed and the form is now read-only.';
      default:
        return 'This game form is no longer available for editing.';
    }
  };

  const getStatusIcon = () => {
    switch (gameStatus) {
      case 'expired':
      case 'missed':
        return <AlertTriangle className="h-8 w-8 text-destructive" />;
      case 'completed':
        return <Clock className="h-8 w-8 text-muted-foreground" />;
      default:
        return <AlertTriangle className="h-8 w-8 text-destructive" />;
    }
  };

  return (
    <Card className="w-full border-destructive/50 bg-destructive/5">
      <CardHeader className="text-center pb-4">
        <div className="flex flex-col items-center space-y-4">
          {getStatusIcon()}
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold text-destructive">
              Form No Longer Available
            </CardTitle>
            <Badge 
              variant="destructive" 
              className="text-sm px-3 py-1"
              style={{ backgroundColor: gameStatusInfo.color }}
            >
              {gameStatusInfo.label}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Game Details */}
        <div className="bg-background/50 rounded-lg p-4 space-y-2">
          <h3 className="font-semibold text-lg">
            {gameData.game_title || gameData.game_name}
          </h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                {formatGameDateTime(gameData.game_date, gameData.game_time).fullDateTime}
              </span>
            </div>
            {gameData.venue_address && (
              <div className="text-xs">
                📍 {gameData.venue_address}
              </div>
            )}
          </div>
        </div>

        {/* Status Message */}
        <div className="text-center space-y-4">
          <p className="text-muted-foreground text-lg">
            {getStatusMessage()}
          </p>
          
          {gameStatus === 'expired' || gameStatus === 'missed' ? (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                What happened?
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Game forms automatically expire when the scheduled game time passes. 
                This ensures data integrity and prevents conflicts after games have started or concluded.
              </p>
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/my-game-forms')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to My Forms
          </Button>
          
          {gameStatus === 'completed' && (
            <Button 
              variant="secondary" 
              onClick={() => navigate(`/games/${gameData.id}/details`)}
            >
              View Game Results
            </Button>
          )}
        </div>

        {/* Additional Help */}
        <div className="text-center text-sm text-muted-foreground pt-4 border-t">
          Need help? Contact your game administrator or venue coordinator.
        </div>
      </CardContent>
    </Card>
  );
};